/**
 * Check-in new revision: single async thunk, slice holds isLoading, isError, message, data.
 * POST api/check-in/new-revision. Create pattern – no heavy dispatch logic.
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import UserAuth from "../../../auth/userAuth";
import AuthTeams from "../../../auth/AuthTeams";
import { postCheckinNewRevision } from "../../api/documentsAPI";
import { serializeError } from "../../utils/serializeError";
import { sanitizeFileName } from "../../../functions/helperMethod";

const userAuth = new UserAuth();

function resolveDocFormat(providedFormat) {
  if (providedFormat != null && String(providedFormat).trim() !== "") {
    return String(providedFormat).trim();
  }

  return "";
}

/**
 * @param {object} payload
 * @param {object} payload.selectedRevisionItem - { class, docNo, docRev, sheetNo, title, format }
 * @param {string} payload.newRevision
 * @param {string} payload.newRevisionText
 * @param {Array<{ name: string, base64str?: string }>} payload.uploadFiles - file name (and optional base64); or use `fileUrl` for server-side download
 * @param {string} [payload.fileUrl] - when set, backend downloads main document bytes (omit base64 on uploadFiles[0])
 * @param {boolean} payload.createViewCopy
 * @param {Array} payload.newRevisionObjCon
 * @param {number} payload.uploadViewCopyCount
 * @param {boolean} payload.viewRequired
 * @param {string} payload.docReleaseStatus
 * @param {Array<{ name: string, base64str: string }>} [payload.viewCopyFiles] - uploaded view copy file(s); when provided, sent in same request so backend uses this file (e.g. for non-Word) instead of auto-creating one
 */
export const checkinNewRevisionThunk = createAsyncThunk(
  "checkinNewRevision/post",
  async (payload, { rejectWithValue }) => {
    const {
      selectedRevisionItem,
      newRevision,
      newRevisionText,
      uploadFiles = [],
      fileUrl,
      createViewCopy,
      newRevisionObjCon,
      uploadViewCopyCount = 0,
      viewRequired,
      docReleaseStatus,
      viewCopyFiles = [],
    } = payload ?? {};

    const usesFileUrl = typeof fileUrl === "string" && fileUrl.trim().length > 0;

    if (!selectedRevisionItem?.class || !uploadFiles?.length) {
      return rejectWithValue({ message: "Selected document and at least one file required" });
    }

    const uploadFile = uploadFiles[0];
    if (!usesFileUrl && !uploadFile?.base64str) {
      return rejectWithValue({ message: "File data or file URL required" });
    }

    try {
      const tokenResponse = await userAuth.getUserToken(true);
      const accessToken = tokenResponse?.accessToken ?? null;
      if (!accessToken) {
        return rejectWithValue({ message: "No access token" });
      }

      const docInfo = {
        checkinViewCopy: createViewCopy,
        docClass: selectedRevisionItem.class,
        docNo: selectedRevisionItem.docNo,
        docRev: selectedRevisionItem.docRev,
        docSheet: selectedRevisionItem.sheetNo,
        title: selectedRevisionItem.title ?? "",
        docFormat: resolveDocFormat(
          selectedRevisionItem.format == null ? "" : selectedRevisionItem.format
        ),
        ...(usesFileUrl
          ? { fileUrl: fileUrl.trim(), fileData: "" }
          : { fileData: uploadFile.base64str }),
        fileName: sanitizeFileName(uploadFile.name),
        objectConnections: newRevisionObjCon ?? [],
        createViewCopy: !!createViewCopy,
        rollbackRevisionOnFail: !!viewRequired,
        webUser: new AuthTeams().getEmail(),
        status:
          docReleaseStatus === false || docReleaseStatus === ""
            ? ""
            : uploadViewCopyCount > 0
              ? ""
              : docReleaseStatus ?? "",
      };

      const viewCopyFile = Array.isArray(viewCopyFiles) && viewCopyFiles.length > 0 && viewCopyFiles[0]?.base64str
        ? viewCopyFiles[0]
        : null;
      if (viewCopyFile) {
        docInfo.viewCopyFileData = viewCopyFile.base64str;
        docInfo.viewCopyFileName = sanitizeFileName(viewCopyFile.name);
      }

      const body = {
        documentRevision: docInfo,
        newRevision: newRevision ?? "",
        newRevisionText: newRevisionText ?? "",
      };

     

      const res = await postCheckinNewRevision(accessToken, body);
      const { data, status } = res || {};

      if (status === 200 && data && typeof data === "object") {
        return { data, status };
      }

      return rejectWithValue({
        status: status || 500,
        message: typeof data === "string" ? data : "Check-in failed",
        data: data,
      });
    } catch (err) {
      // setTimeout(() => {
      //   resetCheckinNewRevision();
      // }, 3000);
      return rejectWithValue(serializeError(err));
    }
  }
);

const initialState = {
  data: null,
  isLoading: false,
  isError: false,
  message: "",
  isDialogOpen: false,
  /**
   * One-time navigation flag.
   * Set to true only when the thunk succeeds, then consumed by the UI to avoid
   * redirect loops when cached success state remains.
   */
  navigateToAllDocuments: false,
};

const checkinNewRevisionSlice = createSlice({
  name: "checkinNewRevision",
  initialState,
  reducers: {
    resetCheckinNewRevision: (state) => {
      state.data = null;
      state.isLoading = false;
      state.isError = false;
      state.message = "";
      state.isDialogOpen = false;
      state.navigateToAllDocuments = false;
    },
    consumeNavigateToAllDocuments: (state) => {
      state.navigateToAllDocuments = false;
    },
    setCheckinDialogOpen: (state, action) => {
      state.isDialogOpen = action.payload === true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkinNewRevisionThunk.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.message = "";
        state.isDialogOpen = true;
        state.navigateToAllDocuments = false;
        state.data =null;
      })
      .addCase(checkinNewRevisionThunk.fulfilled, (state, action) => {
        state.isDialogOpen = false;
        state.isLoading = false;
        state.isError = false;
        state.message = "Check-in new revision successful.";
        state.data = action.payload?.data ?? null;
        state.navigateToAllDocuments = true;
      })
      .addCase(checkinNewRevisionThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload?.message ?? action.error?.message ?? "Check-in new revision failed.";
        state.data = null;
        state.isDialogOpen = true;
        state.navigateToAllDocuments = false;
      });
  },
});

export const {
  resetCheckinNewRevision,
  setCheckinDialogOpen,
  consumeNavigateToAllDocuments,
} = checkinNewRevisionSlice.actions;
export default checkinNewRevisionSlice.reducer;
